import React, { useEffect, useState } from "react";
import {
  Table,
  Select,
  Pagination,
  Spin,
  Modal,
  Button,
  message,
  Input,
  Typography,
  Alert,
  Row,
  Col,
  Card,
} from "antd";
import axios from "axios";
import { config } from "../../config";
import { LoadingOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "./Infografik.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const { Title, Text } = Typography;
const { Search } = Input;

const CACHE_KEY = "infografik_thumbnails_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 menit

const Infografik = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const [selectedSubjek, setSelectedSubjek] = useState("Semua");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const uniqueSubjects = ["Semua", ...new Set(data.map((item) => item.subjek))];

  const filteredBySubject = filtered.filter((item) =>
    selectedSubjek === "Semua" ? true : item.subjek === selectedSubjek
  );

  const paginatedData = filteredBySubject.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Fungsi helper untuk cache
  const loadCachedThumbnails = () => {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const now = Date.now();
      if (now - parsed.timestamp > CACHE_DURATION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed.thumbs || {};
    } catch (e) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const saveThumbnailsToCache = (thumbsMap) => {
    try {
      const payload = {
        timestamp: Date.now(),
        thumbs: thumbsMap,
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("LocalStorage penuh, membersihkan cache lama...");
      localStorage.removeItem(CACHE_KEY);
    }
  };

  const getPdfThumbnail = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      
      // Gunakan scale kecil (0.3 - 0.5) agar string Base64 tidak terlalu panjang
      const viewport = page.getViewport({ scale: 0.4 }); 
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      // Simpan sebagai JPEG (lebih ringan dari PNG) untuk localStorage
      return canvas.toDataURL("image/jpeg", 0.7); 
    } catch (error) {
      console.error("Gagal generate thumbnail:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchInfografik = async () => {
      try {
        const currentURL = window.location.href;
        const res = await axios.get(
          `${config.BASE_URL}organization/findContentOrganization?namaDomain=${currentURL}`
        );

        const InfografikList = res.data?.data?.infografis || [];
        if (InfografikList.length === 0) {
          setError("Belum ada infografik.");
          setLoading(false);
          return;
        }

        setData(InfografikList);
        setFiltered(InfografikList);

        // --- LOGIC CACHE PROGRESIF ---
        const cachedThumbs = loadCachedThumbnails() || {};
        
        // 1. Tampilkan yang ada di cache dulu biar instan
        setThumbnails(cachedThumbs);
        setLoading(false); // Matikan loading utama segera setelah data teks muncul

        // 2. Cari mana yang belum punya thumbnail
        const updatedThumbs = { ...cachedThumbs };
        let hasNewThumb = false;

        // 3. Proses satu per satu (tidak menunggu semua selesai / non-blocking)
        for (const item of InfografikList) {
          const id = item.infografikDesaCantikId;
          
          if (!updatedThumbs[id] && item.file) {
            const pdfUrl = `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(item.file)}`;
            const thumb = await getPdfThumbnail(pdfUrl);
            
            if (thumb) {
              updatedThumbs[id] = thumb;
              hasNewThumb = true;
              
              // Update state secara real-time per gambar yang selesai
              setThumbnails((prev) => ({
                ...prev,
                [id]: thumb,
              }));
            }
          }
        }

        // 4. Simpan ke cache jika ada update baru
        if (hasNewThumb) {
          saveThumbnailsToCache(updatedThumbs);
        }
        
      } catch (err) {
        setError("Gagal memuat data.");
        setLoading(false);
      }
    };

    fetchInfografik();
  }, []);

  const onSearch = (value) => {
    setKeyword(value);
    const lower = value.toLowerCase();
    const result = data.filter((item) =>
      item.subjek?.toLowerCase().includes(lower)
    );
    setFiltered(result);
  };

  const downloadPdf = (url, subjek) => {
    const link = document.createElement("a");
    link.href = url;
    const safeFilename = `${subjek}`.replace(/[<>:"/\\|?*]+/g, "").trim();
    link.setAttribute("download", `${safeFilename || "Infografik"}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewPdf = (id, file) => {
    const cachedPreview = thumbnails[id];
    const fullPdfUrl = `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(file)}`;
    
    // Kita set URL PDF asli untuk tombol "Buka PDF Asli" jika perlu, 
    // tapi untuk tampilan modal kita pakai thumbnail agar INSTAN.
    setPdfBlobUrl({
      url: fullPdfUrl,
      preview: cachedPreview
    });
    setPdfModalVisible(true);
  };

  return (
    <div className="infografik-wrapper">
      <Title level={2} style={{ textAlign: "center", marginBottom: 20 }}>
        Infografis Desa Cantik
      </Title>

      <Row gutter={24}>
        <Col xs={24} sm={6} md={5}>
          <div className="subject-list">
            <Title level={5}>Subjek</Title>
            <ul className="subject-ul">
              {uniqueSubjects.map((subject) => (
                <li
                  key={subject}
                  className={subject === selectedSubjek ? "active" : ""}
                  onClick={() => {
                    setSelectedSubjek(subject);
                    setPage(1);
                  }}
                >
                  {subject}
                </li>
              ))}
            </ul>
          </div>
        </Col>

        <Col xs={24} sm={18} md={19}>
          {loading ? (
            <div className="infografik-loading">
              <Spin
                indicator={<LoadingOutlined className="orange-spin" spin />}
                size="large"
              />
            </div>
          ) : error ? (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: 600, margin: "0 auto" }}
            />
          ) : (
            <>
              <Row gutter={[16, 16]}>
                {paginatedData.length === 0 ? (
                  <Col span={24}>
                    <Text type="secondary">
                      Tidak ada infografis ditemukan.
                    </Text>
                  </Col>
                ) : (
                  paginatedData.map((item) => {
                    const cover = thumbnails[item.infografikDesaCantikId];
                    return (
                      <Col
                        key={item.infografikDesaCantikId}
                        xs={24}
                        sm={12}
                        md={8}
                      >
                        <Card
                          hoverable
                          onClick={() => previewPdf(item.infografikDesaCantikId, item.file)}
                          className="infografik-card"
                          cover={
                            <div className="card-cover" style={{ background: '#f5f5f5', height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              {cover ? (
                                <img 
                                  src={cover} 
                                  alt="PDF Cover" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                />
                              ) : (
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#fa8c16' }} spin />} />
                              )}
                            </div>
                          }
                        >
                          <Title level={5} style={{ margin: "8px 0" }}>
                            {item.subjek || "subjek tidak tersedia"}
                          </Title>
                          <Button
                            block
                            type="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadPdf(
                                `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(item.file)}`,
                                item.subjek || "Infografik"
                              );
                            }}
                          >
                            Unduh Infografis
                          </Button>
                        </Card>
                      </Col>
                    );
                  })
                )}
              </Row>

              <div style={{ textAlign: "center", marginTop: 24 }}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={filteredBySubject.length}
                  onChange={(p) => setPage(p)}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </Col>
      </Row>

      <Modal
        title="Preview Infografik"
        open={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          setPdfBlobUrl(null);
        }}
        footer={[
          <Button key="download" type="primary" onClick={() => downloadPdf(pdfBlobUrl?.url, "Infografik")}>
            Unduh PDF
          </Button>
        ]}
        width={800}
        centered
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '300px' }}>
          {pdfBlobUrl?.preview ? (
            <img 
              src={pdfBlobUrl.preview} 
              alt="Preview" 
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
              }} 
            />
          ) : (
            <div style={{ padding: '50px' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
              <p style={{ marginTop: 16 }}>Memuat preview...</p>
            </div>
          )}
          
          <Text type="secondary" style={{ marginTop: 16 }}>
            Menampilkan halaman pertama. Gunakan tombol unduh untuk melihat dokumen lengkap.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default Infografik;
